import apiRequest from "src/utils/api-request";
import withApiPath from "src/utils/with-api-path";

export interface UserDetails {
	uuid: string;
	email: string;
	name: string;
	profileImageUrl?: string;
	createdAt: string;
	deletedAt?: string;
	blockedAt?: string;
	blockedReason?: string;
	merchantCount: number;
}

export interface PaginatedUsersResponse {
	users: UserDetails[];
	totalCount: number;
	limit: number;
	offset: number;
}

export interface ListUsersParams {
	limit?: number;
	offset?: number;
	search?: string;
	includeDeleted?: boolean;
}

export interface StatsResponse {
	totalUsers: number;
	totalAdmins: number;
	pendingInvites: number;
	deletedUsers: number;
}

export interface UserSettings {
	serviceFeeRate?: number;
	maxMerchants?: number;
}

export interface UpdateUserSettingsRequest {
	serviceFeeRate?: number;
	maxMerchants?: number;
}

export interface BlockUserRequest {
	reason: string;
}

export interface Invitation {
	id: number;
	email: string;
	status: string;
	expiresAt: string;
	createdAt: string;
	acceptedAt?: string;
}

export interface PaginatedInvitationsResponse {
	invitations: Invitation[];
	totalCount: number;
	limit: number;
	offset: number;
}

export interface CreateInvitationRequest {
	email: string;
}

export interface MerchantDetails {
	uuid: string;
	name: string;
	website: string;
	createdAt: string;
	updatedAt: string;
	deletedAt?: string;
	creatorId: number;
	creatorName?: string;
	creatorEmail?: string;
}

export interface PaginatedMerchantsResponse {
	merchants: MerchantDetails[];
	totalCount: number;
	limit: number;
	offset: number;
}

export interface ListMerchantsParams {
	limit?: number;
	offset?: number;
	search?: string;
	includeDeleted?: boolean;
}

export interface UserWithdrawalFee {
	ticker: string;
	fixedUsd?: number;
	percentage?: number;
	feeProfile: string | null;
}

export interface UpsertUserWithdrawalFeeRequest {
	ticker: string;
	fixedUsd?: number;
	percentage?: number;
	feeProfile?: string | null;
}

const userProvider = {
	async listUsers(
		params: ListUsersParams = {},
	): Promise<PaginatedUsersResponse> {
		const response = await apiRequest.get(withApiPath("/users"), {
			params: {
				limit: params.limit ?? 20,
				offset: params.offset ?? 0,
				search: params.search ?? "",
				includeDeleted: params.includeDeleted ?? false,
			},
		});
		return response.data;
	},

	async getUser(userId: string): Promise<UserDetails> {
		const response = await apiRequest.get(withApiPath(`/users/${userId}`));
		return response.data;
	},

	async getStats(): Promise<StatsResponse> {
		const response = await apiRequest.get(withApiPath("/stats"));
		return response.data;
	},

	async getUserSettings(userId: string): Promise<UserSettings> {
		const response = await apiRequest.get(
			withApiPath(`/users/${userId}/settings`),
		);
		return response.data;
	},

	async updateUserSettings(
		userId: string,
		settings: UpdateUserSettingsRequest,
	): Promise<UserSettings> {
		const response = await apiRequest.put(
			withApiPath(`/users/${userId}/settings`),
			settings,
		);
		return response.data;
	},

	async blockUser(userId: string, reason: string): Promise<void> {
		await apiRequest.post(withApiPath(`/users/${userId}/block`), { reason });
	},

	async unblockUser(userId: string): Promise<void> {
		await apiRequest.post(withApiPath(`/users/${userId}/unblock`));
	},

	async listInvitations(params: {
		limit?: number;
		offset?: number;
		status?: string;
	}): Promise<PaginatedInvitationsResponse> {
		const response = await apiRequest.get(withApiPath("/invitations"), {
			params: {
				limit: params.limit ?? 20,
				offset: params.offset ?? 0,
				status: params.status ?? "",
			},
		});
		return response.data;
	},

	async createInvitation(email: string): Promise<Invitation> {
		const response = await apiRequest.post(withApiPath("/invitations"), {
			email,
		});
		return response.data;
	},

	async revokeInvitation(invitationId: number): Promise<void> {
		await apiRequest.delete(withApiPath(`/invitations/${invitationId}`));
	},

	async resendInvitation(invitationId: number): Promise<void> {
		await apiRequest.post(withApiPath(`/invitations/${invitationId}/resend`));
	},

	async listMerchants(
		params: ListMerchantsParams = {},
	): Promise<PaginatedMerchantsResponse> {
		const response = await apiRequest.get(withApiPath("/merchants"), {
			params: {
				limit: params.limit ?? 20,
				offset: params.offset ?? 0,
				search: params.search ?? "",
				includeDeleted: params.includeDeleted ?? false,
			},
		});
		return response.data;
	},

	async getUserMerchants(userId: string): Promise<MerchantDetails[]> {
		const response = await apiRequest.get(
			withApiPath(`/users/${userId}/merchants`),
		);
		return response.data;
	},

	async getUserWithdrawalFees(userId: string): Promise<UserWithdrawalFee[]> {
		const response = await apiRequest.get(
			withApiPath(`/users/${userId}/withdrawal-fees`),
		);
		return response.data;
	},

	async upsertUserWithdrawalFee(
		userId: string,
		fee: UpsertUserWithdrawalFeeRequest,
	): Promise<UserWithdrawalFee> {
		const response = await apiRequest.put(
			withApiPath(`/users/${userId}/withdrawal-fees`),
			fee,
		);
		return response.data;
	},

	async deleteUserWithdrawalFee(
		userId: string,
		ticker: string,
		feeProfile?: string | null,
	): Promise<void> {
		const params = feeProfile ? `?feeProfile=${feeProfile}` : "";
		await apiRequest.delete(
			withApiPath(`/users/${userId}/withdrawal-fees/${ticker}${params}`),
		);
	},
};

export default userProvider;
